import React, { useMemo, useState, useEffect } from 'react';
import AssetDetail from './AssetDetail';
import { useFreemium } from '../hooks/useFreemium';
import { cleanTicker, logoTicker, fmtPrice, fmt, getExchange } from '../utils/format';
import { TICKER_FALLBACK } from '../config/constants';
import './HomeView.css';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Gute Nacht';
  if (h < 12) return 'Guten Morgen';
  if (h < 17) return 'Guten Tag';
  if (h < 21) return 'Guten Abend';
  return 'Gute Nacht';
}

// Recommendation row avatar with logo
function RecAvatar({ ticker }) {
  const [imgFailed, setImgFailed] = useState(false);
  const sym = cleanTicker(ticker);
  const logoUrl = `https://assets.parqet.com/logos/symbol/${logoTicker(ticker)}`;
  const fallback = TICKER_FALLBACK[ticker] || { bg: '#2563eb', color: '#fff' };

  if (!imgFailed) {
    return (
      <div className="hv-rec-circle hv-rec-circle--logo">
        <img src={logoUrl} alt={sym} onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div className="hv-rec-circle" style={{ background: fallback.bg, color: fallback.color }}>
      {sym.slice(0, 3)}
    </div>
  );
}

// Featured card avatar — rounded square with logo
function FeaturedAvatar({ ticker }) {
  const [imgFailed, setImgFailed] = useState(false);
  const sym = cleanTicker(ticker);
  const logoUrl = `https://assets.parqet.com/logos/symbol/${logoTicker(ticker)}`;
  const fallback = TICKER_FALLBACK[ticker] || { bg: '#2563eb', color: '#fff' };

  if (!imgFailed) {
    return (
      <div className="hv-feat-avatar">
        <img src={logoUrl} alt={sym} onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div className="hv-feat-avatar hv-feat-avatar--fb"
      style={{ background: fallback.bg, color: fallback.color }}>
      {sym.slice(0, 2)}
    </div>
  );
}

function HomeView({ assets, loading, userName, onAddToPortfolio, onTabChange, onRegisterDetailCloser }) {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { getLimit } = useFreemium();
  const reboundActive = (() => {
    try { return localStorage.getItem('bullivo_rebound_mode') === '1'; } catch { return false; }
  })();

  useEffect(() => {
    onRegisterDetailCloser?.(selectedAsset ? () => setSelectedAsset(null) : null);
  }, [selectedAsset, onRegisterDetailCloser]);

  const buyAssets = useMemo(() =>
    assets.filter(a => a.signal === 'Kaufen'),
    [assets]
  );

  const featuredAsset = buyAssets[0] ?? null;
  const homeLimit = getLimit('homeSignals');
  const maxRecs = homeLimit != null ? Math.max(homeLimit - 1, 0) : 2;
  const recommendations = buyAssets.slice(1, 1 + maxRecs);
  const strongCount = buyAssets.filter(a => (a.confidence ?? 0) >= 70).length;

  return (
    <div className="home-view">

      {/* ── Greeting: Stitch Layout ── */}
      <div className="hv-greeting">
        <p className="hv-greeting-sub">
          {userName ? `${getGreeting()}, ${userName}` : getGreeting()}
        </p>
        <h2 className="hv-greeting-title">
          {loading ? (
            'Signale werden geladen…'
          ) : (
            <><span className="hv-greeting-count">{strongCount}</span> neue starke Signale</>
          )}
        </h2>
      </div>

      {loading ? (
        <div className="hv-skeletons">
          <div className="hv-skeleton hv-skeleton--feat shimmer" />
          {[1,2,3].map(i => <div key={i} className="hv-skeleton shimmer" />)}
        </div>
      ) : !featuredAsset ? (
        <div className="hv-empty">
          <p>Aktuell keine starken Kaufsignale.</p>
          <button className="hv-empty-btn" onClick={() => onTabChange?.('signale')}>
            Alle Signale ansehen
          </button>
        </div>
      ) : (
        <>
          {/* ── Featured Card: Stitch blue-outline ── */}
          <div className={`hv-featured${reboundActive ? ' hv-featured--rebound' : ''}`} onClick={() => setSelectedAsset(featuredAsset)}>
            <div className="hv-featured-top">
              <FeaturedAvatar ticker={featuredAsset.ticker} />
              <div className="hv-featured-identity">
                <span className="hv-featured-name">{featuredAsset.name ?? featuredAsset.ticker}</span>
                <span className="hv-featured-sector">
                  {featuredAsset.reasons?.[0] || `${(featuredAsset.sector || 'Equity').toUpperCase()} • ${getExchange(featuredAsset.ticker)}`}
                </span>
              </div>
            </div>

            <div className="hv-featured-stats">
              <div className="hv-stat-box">
                <span className="hv-stat-label">SIGNAL-STÄRKE</span>
                <div className="hv-stat-row">
                  <span className="hv-stat-value">{featuredAsset.confidence ?? 0}%</span>
                  <div className="hv-conf-bar">
                    <div className="hv-conf-fill" style={{ width: `${featuredAsset.confidence ?? 0}%` }} />
                  </div>
                </div>
              </div>
              <div className="hv-stat-box">
                <span className="hv-stat-label">KURSZIEL</span>
                <span className="hv-stat-value">
                  {(() => {
                    const target = featuredAsset.targetMeanPrice;
                    const price = featuredAsset.price;
                    if (target && price && price > 0) {
                      const pct = ((target - price) / price) * 100;
                      return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;
                    }
                    return '—';
                  })()}
                </span>
              </div>
            </div>

            <button className="hv-featured-cta">Details ansehen</button>
          </div>

          {/* ── Weitere Empfehlungen ── */}
          {recommendations.length > 0 && (
            <div className="hv-recs">
              <h3 className="hv-recs-title">Weitere Empfehlungen</h3>
              <div className="hv-recs-list">
                {recommendations.map(asset => {
                  const chg = asset.change ?? 0;
                  return (
                    <button key={asset.ticker} className="hv-rec-row" onClick={() => setSelectedAsset(asset)}>
                      <RecAvatar ticker={asset.ticker} />
                      <div className="hv-rec-info">
                        <span className="hv-rec-name">{asset.name?.split(' ').slice(0, 2).join(' ') ?? asset.ticker}</span>
                        <span className="hv-rec-sub">{asset.ticker}</span>
                      </div>
                      <div className="hv-rec-right">
                        <span className="hv-rec-price">{fmtPrice(asset.price)}</span>
                        <span className={`hv-rec-chg ${chg >= 0 ? 'pos' : 'neg'}`}>{fmt(chg)}</span>
                      </div>
                      <span className="hv-rec-chevron">›</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {selectedAsset && (
        <AssetDetail
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onAddToPortfolio={onAddToPortfolio}
        />
      )}

    </div>
  );
}

export default HomeView;
